import { useMemo, useEffect, useRef } from 'react';
import { AppLink } from '@/shared/components/AppLink';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from '@/shared/widgets/carousel';
import { getCenterSlideIndex } from '@/shared/utils/carouselUtils';
import { VIDEO_PLAY_OBSERVER_OPTIONS } from '@/core/config/intersectionObserver';

interface ReelItem {
  title: string;
  url: string;
  posterUrl?: string;
  link: string;
  productId?: string;
  productName?: string;
  position?: number;
  isVideo?: boolean;
  autoplay?: string;
  playsinline?: string;
  loop?: string;
  muted?: string;
  lazyload?: string;
  shouldRefresh?: boolean;
}

interface ReelsSectionProps {
  data: Record<string, unknown> & {
    items?: ReelItem[];
    blankImageUrl?: string;
  };
  sectionKey: string;
  eventLabel?: string;
  priority?: number;
}

type ReelCardVariant = 'mobile' | 'desktop';

function ReelCardContent({
  item,
  index,
  blankImageUrl,
  videoRefSetter,
  variant,
}: {
  item: ReelItem;
  index: number;
  blankImageUrl?: string;
  videoRefSetter: (index: number, el: HTMLVideoElement | null) => void;
  variant: ReelCardVariant;
}) {
  const aspectClass = variant === 'mobile' ? 'aspect-[9/12.8]' : 'aspect-[9/14]';
  const videoWrapperClass = variant === 'desktop' ? 'rounded-lg bg-[#9D7792]' : 'rounded-lg';

  return (
    <div className="flex flex-col h-full">
      {item.title && (
        <div className="text-sm font-medium mb-2 text-[#ACACAC]">
          {item.title}
        </div>
      )}
      <AppLink
        to={item.link}
        eventName="Reel Card Clicked"
        eventProperties={{
          'Reel Card Name': item.title,
          Position: index + 1,
          'Product Id': item.productId ?? null,
          'Product Name': item.productName ?? null,
        }}
        className="block relative group hover:opacity-90 transition-opacity"
        shouldRefresh={item.shouldRefresh}
      >
        {item.isVideo && item.url ? (
          <div className={`relative w-full ${aspectClass} overflow-hidden ${videoWrapperClass}`}>
            <video
              ref={(el) => videoRefSetter(index, el)}
              poster={item.posterUrl || blankImageUrl}
              className="w-full h-full object-cover rounded-[10px] border-2 border-[#9D7792] bg-[#9D7792]"
              title={item.title}
              width="100%"
              height="auto"
              playsInline={!!item.playsinline}
              loop={!!item.loop}
              muted={!!item.muted}
              preload={item.lazyload === 'lazyload' ? 'none' : 'auto'}
            >
              <source src={item.url} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        ) : null}
      </AppLink>
    </div>
  );
}

export default function ReelsSection({
  data,
}: ReelsSectionProps) {
  const items = useMemo(() => (data.items || []) as ReelItem[], [data.items]);
  const videoRefs = useRef<Map<number | string, HTMLVideoElement>>(new Map());
  const desktopScrollRef = useRef<HTMLDivElement>(null);

  // Calculate initial slide index (center slide)
  const initialSlideIndex = useMemo(() => getCenterSlideIndex(items.length), [items.length]);

  // Register video refs (for both mobile and desktop)
  const setVideoRef = (index: number, element: HTMLVideoElement | null) => {
    if (element) {
      videoRefs.current.set(index, element);
    } else {
      videoRefs.current.delete(index);
    }
  };

  // Register desktop video refs with a different key
  const setDesktopVideoRef = (index: number, element: HTMLVideoElement | null) => {
    if (element) {
      videoRefs.current.set(`desktop-${index}`, element);
    } else {
      videoRefs.current.delete(`desktop-${index}`);
    }
  };

  // Set up IntersectionObserver for each video to auto-play when fully in viewport
  useEffect(() => {
    if (videoRefs.current.size === 0) return;

    const observers: IntersectionObserver[] = [];

    videoRefs.current.forEach((video, index) => {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio >= 1.0) {
              // Video is fully in viewport - play it
              video.play().catch((error) => {
                console.warn(`ReelsSection - Video ${index} play error:`, error);
              });
            } else {
              // Video is not fully visible - pause it
              video.pause();
            }
          });
        },
        VIDEO_PLAY_OBSERVER_OPTIONS
      );

      observer.observe(video);
      observers.push(observer);
    });

    return () => {
      observers.forEach((observer) => observer.disconnect());
    };
  }, [items.length]);


  if (!items || items.length === 0) {
    return null;
  }

  return (
    <section className="py-8 md:py-12 w-full">
      {/* Mobile: Carousel - same as collection section */}
      {items.length > 0 && (
        <Carousel
          opts={{
            align: 'center',
            loop: false,
            slidesToScroll: 1,
            containScroll: false,
            startIndex: initialSlideIndex,
          }}
          className="w-full md:hidden"
        >
          <CarouselContent className="-ml-0 gap-2.5">
            {items.map((item, index) => (
              <CarouselItem
                key={`reel-${index}`}
                className="pl-0 basis-auto flex-shrink-0"
                style={{ width: '50vw', minWidth: '50vw', maxWidth: '50vw' }}
              >
                <ReelCardContent
                  item={item}
                  index={index}
                  blankImageUrl={data.blankImageUrl as string | undefined}
                  videoRefSetter={setVideoRef}
                  variant="mobile"
                />
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
      )}

      {/* Desktop: Horizontal scroll container (no swipe) */}
      {items.length > 0 && (
        <div 
          ref={desktopScrollRef}
          className="hidden md:block w-full overflow-x-auto container-fluid"
          style={{ scrollbarWidth: 'thin' }}
        >
          <div className="flex gap-1 min-w-max">
            {items.map((item, index) => (
              <div
                key={`desktop-reel-${index}`}
                data-reel-index={index}
                className="flex-shrink-0"
                style={{ width: '255px', minWidth: '255px', maxWidth: '255px' }}
              >
                <ReelCardContent
                  item={item}
                  index={index}
                  blankImageUrl={data.blankImageUrl as string | undefined}
                  videoRefSetter={setDesktopVideoRef}
                  variant="desktop"
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

